import React, { useState, useEffect, useRef } from 'react';
import * as Yup from 'yup';
import { Formik, Field } from 'formik';
import './kyc-form.scss';
import BeatLoader from "react-spinners/BeatLoader";
import Constant from "../../../Constant";
import { verifyKyc, getUserInfo, verifyAddress, verifyProfession } from '../../../services/users';
import { v4 as uuid } from "uuid";
import { toast } from 'react-toastify';

import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Button,
} from '@mui/material';
import { ref, storage } from "../../../firebase/firebase-config";
import { getDownloadURL, uploadBytes } from "firebase/storage";
import { BsCameraFill, BsCheck, BsCheck2Circle, BsChevronDoubleRight, BsChevronLeft, BsChevronRight, BsPersonCheck } from 'react-icons/bs';
import logo from "../../../images/logo-trans.png";
import Webcam from 'react-webcam';
import { countries } from '../../../utils/http-client';

const verifyInfoSchema = Yup.object().shape({
  email: Yup.string()
    .email("Enter a valid email address")
    .required("Email is required"),
  firstName: Yup.string()
    .required("First name is required"),
  lastName: Yup.string()
    .required("Last name is required")
});

const verifyAddressSchema = Yup.object().shape({
  address: Yup.string()
    .required("Address is required"),
  city: Yup.string()
    .required("City is required"),
  country: Yup.string()
    .required("Country is required")
});

const steps = ['Personal Info', 'Address Info', 'Professional Info'];

const KycForm = ({ idToken }) => {

  const [successMessage, setSuccessMessage] = useState('');
  const [first_name, setFirstName] = useState('');
  const [last_name, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [secondaryAddress, setSecondaryAddress] = useState('');
  const [city, setCity] = useState('');
  const [postCode, setPostCode] = useState('');
  const [country, setCountry] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(logo);
  const [selfieImagePreview, setSelfieImagePreview] = useState(logo);
  const webcamRef = useRef(null);
  const [activeStep, setActiveStep] = useState(0);
  const [idFile, setIdFile] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);
  const [addressFile, setAddressFile] = useState(null);
  const [addressFilePlaceholder, setAddressFilePlaceholder] = useState('');
  const [professionalFile, setProfessionalFile] = useState(null);
  const [professionalFilePlaceholder, setProfessionalFilePlaceholder] = useState('');

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  }

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  }

  const handleSkip = () => {
    setActiveStep(steps.length - 1);
  }
  
  const handleImageIDPreview = (event) => {
    const file = event.target.files[0];

    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);  
        setIdFile(file);      
      };

      reader.readAsDataURL(file);
    } else {
      setImagePreview(logo);
      return;
    }
  }
  
  const handleAddressFile = (event) => {
    const file = event.target.files[0];

    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        // console.log('File name: ', file.name);
        setAddressFile(file);
        setAddressFilePlaceholder(file.name);
      };

      reader.readAsDataURL(file);
    } else {
      return;
    }
  }
  
  const handleProfessionalFile = (event) => {
    const file = event.target.files[0];

    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfessionalFile(file);
        setProfessionalFilePlaceholder(file.name);
      };

      reader.readAsDataURL(file);
    } else {
      return;
    }
  }
  
  const handleImageSelfiePreview = (event) => {
    const screenshot = webcamRef.current.getScreenshot();

    if (screenshot) {
      setSelfieImagePreview(screenshot);
      setSelfieFile(screenshot);
    } else {
      setSelfieImagePreview(logo);
      return;
    }
  }

  const handleSubmitIdVerification = (values) => {
    setIsLoading(true);
    const fileName = idFile?.name;
    if(fileName === undefined || fileName === null ){
      toast.warning('You have not uploaded an ID. Please check and try again');
      setIsLoading(false);
      return;
    }
    // const selfieName = selfieFile.name;
    const extension = fileName.substr((~-fileName.lastIndexOf(".") >>> 0) + 2);
    const unique_id = uuid();
    const selfieExtension = 'jpeg';
    const idRef = ref(storage, `kyc/id_docs/${unique_id}.${extension}`);
    const selfieRef = ref(storage, `kyc/id_selfies/${unique_id}.${selfieExtension}`);
    const selfieImageBlob = new Blob([selfieFile], { type: 'image/jpeg' });

    //upload ID first, then selfie
    uploadBytes(idRef, idFile, {
      contentType: `image/${extension}`,
      contentType: `application/${extension}`,
    }).then(() => {
        getDownloadURL(idRef)
          .then((idUrl) => { //upload selfie
            uploadBytes(selfieRef, selfieImageBlob, {
              contentType: `image/${selfieExtension}`
            } ).then(() => {
              getDownloadURL(selfieRef).then(selfieUrl => {
                //push to API
                verifyKyc({...values, idDoc: idUrl, selfieDoc: selfieUrl}).then(res => {
                  if(res.status_code == 200){
                    toast(res.message)
                    handleNext();
                  }else{
                    toast.error(res.message);
                  }
                }).catch( err => {
                  toast.error(err.message);
                }).finally(_ => setIsLoading(false));
              })
            }).catch((err) => {
              toast.error('Error downloading selfie image for verification. Please try again later');
              setIsLoading(false);
            });
          })
          .catch((err) => {
            toast.error(
              `Error downloading ID file for verification. ${err?.message}. Please try again.`
            );
            setIsLoading(false);
          });
      })
      .catch((err) => {
        toast.error(
          `Error uploading ID file for verification. Please try again later`
        );
        setIsLoading(false);
      })
  };
  
  const handleSubmitAddressVerification = (values) => {
    setIsLoading(true);
    console.log('submitting .... ', values);
    const fileName = addressFile?.name;
    if(fileName === undefined || fileName === null ){
      toast.warning('Sorry, You have not uploaded any document. Please check and try again');
      setIsLoading(false);
      return;
    }
    // const selfieName = selfieFile.name;
    const extension = fileName.substr((~-fileName.lastIndexOf(".") >>> 0) + 2);
    const unique_id = uuid();
    const idRef = ref(storage, `kyc/poa/${unique_id}.${extension}`);

    uploadBytes(idRef, idFile, {
      contentType: `image/${extension}`,
      contentType: `application/${extension}`,
    }).then(() => {
        getDownloadURL(idRef)
          .then((addressUrl) => { //upload address doc
            values.addressFile = addressUrl;
            verifyAddress(values).then(result => {
              //
              if(result.status_code == 200 ){
                toast(result.message);
                handleNext();
              }else{
                //address verification not OK!
                toast.error(result.message);
              }
            }).catch( err => {
                  toast.error(err.message);
                })
                .finally(_ => setIsLoading(false));
              })
            })
        .catch((err) => {
          console.log(err);
          toast.error('Error sending address document for verification. Please try again later');
          setIsLoading(false);
        });
  };
  
  const handleSubmitProVerification = (values) => {
    setIsLoading(true);
    console.log('submitting file ... ', values);
    const fileName = professionalFile?.name;
    if(fileName === undefined || fileName === null ){ 
      toast.warning('Sorry, You have not uploaded any document. Please check and try again');
      setIsLoading(false);
      return;
    }
    // const selfieName = selfieFile.name;
    const extension = fileName.substr((~-fileName.lastIndexOf(".") >>> 0) + 2);
    const unique_id = uuid();
    const idRef = ref(storage, `kyc/professional_docs/${unique_id}.${extension}`);

    uploadBytes(idRef, idFile, {
      contentType: `image/${extension}`,
      contentType: `application/${extension}`,
    }).then(() => {
        getDownloadURL(idRef)
          .then((proUrl) => { //upload pro doc
            values.professionalFile = proUrl;
            verifyProfession(values).then(result => {
              
              if(result.status_code == 200 ){
                toast(result.message);
                setSuccessMessage(result.message);
                handleNext();
              }else{
                //pro verification not OK!
                toast.error(result.message);
              }
            }).catch( err => {
                  toast.error(err.message);
                })
                .finally(_ => setIsLoading(false));
              })
            })
        .catch((err) => {
          console.log(err);
          toast.error('Error sending document for verification. Please try again later');
          setIsLoading(false);
        });
  };

  const Content = ({ children }) => {
    return (
      <Box sx={{ p: 2, mt: 3 }}>
        {children}
        {/* <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          {activeStep !== 0 && (
            <Button onClick={handleBack} sx={{ mr: 1 }}><BsChevronLeft />&nbsp;
              Back
            </Button>
          )}
          <Button variant="contained" onClick={handleNext} disabled={activeStep === steps.length - 1}>
            {activeStep === steps.length - 1 ? '' : 'Next'}
          </Button>
        </Box> */}
      </Box>
    );
  }

  useEffect(() => {
    getUserInfo(idToken).then( result => {
      if(result.status_code == 200){
        setFirstName(result.data.firstName);
        setLastName(result.data.lastName);
        setEmail(result.data.email);
        setPhone(result.data.phoneNumber);
        setPostCode(result.data.postCode ?? '');
        setAddress(result.data.address ?? '');
        setSecondaryAddress(result.data.secondaryAddress ?? '');
        setCity(result.data.city ?? '');
        setCountry(result.data.country.name ?? '');
        setImagePreview(result.data.idImage ?? logo);
        setSelfieImagePreview(result.data.idSelfie ?? logo);
      }else{
        toast.error(result.message);
      }
    }).catch(err => {
      toast.error(err);
    });
  }, []);

  return (
    <Box className="kyc-form">
      <Stepper activeStep={activeStep}>
        {steps.map((label, index) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <Content>
        {activeStep === 0 && 
        <Typography>
          <Formik
            initialValues={{
            firstName: first_name,
            lastName: last_name,
            email: email,
            image: "",
            selfieImage: "",
            token: idToken
          }}
          onSubmit={(values) => {
            // console.log(`info values`, values);
            handleSubmitIdVerification(values);
          }}
            validationSchema={verifyInfoSchema}>
            {({ handleSubmit, values, handleChange, errors, touched }) => (
            <form onSubmit={handleSubmit}>
                <div className={`field`}>
                  <label htmlFor="firstName">First Name*</label>
                  <input
                    name="firstName"
                    id="firstName"
                    placeholder="Enter your first name"
                    value={values.firstName}
                    onChange={handleChange}
                    required
                  />
                  {errors.firstName && touched.firstName ? (
                    <div className="error">{errors.firstName}</div>
                  ) : null}
                </div>
                <div className={`field`}>
                  <label htmlFor="lastName">Last Name*</label>
                  <input
                    name="lastName"
                    id="lastName"
                    placeholder="Enter your last name"
                    value={values.lastName}
                    onChange={handleChange}
                    required
                  />
                  {errors.lastName && touched.lastName ? (
                    <div className="error">{errors.lastName}</div>
                  ) : null}
                </div>
                <div className={`field`}>
                  <label htmlFor="phoneNumber">Phone Number(Whatsapp)*</label>
                  <input
                    name="phoneNumber"
                    id="phoneNumber"
                    type='phone'
                    placeholder="Include country code (237xx...)"
                    value={phone}
                    onChange={handleChange}
                    readOnly
                  />
                  {errors.phoneNumber && touched.phoneNumber ? (
                    <div className="error">{errors.phoneNumber}</div>
                  ) : null}
                  {/* <Link to={"/"} className={'forgot-password'}>Forgot password?</Link> */}
                </div>
                <div className={`field`}>
                  <label htmlFor="email">email*</label>
                  <input
                    name="email"
                    id="email"
                    placeholder="Enter Email"
                    value={values.email}
                    required
                    onChange={handleChange}
                  />
                  {errors.email && touched.email ? (
                    <div className="error">{errors.email}</div>
                  ) : null}
                </div>
                <div className="field">
                  <label htmlFor="image">
                    Upload your ID (Government issued ID/Passport/Resident Card)*
                  </label>
                  <img
                    src={imagePreview}
                    alt="ID Image preview"
                    style={{
                      width: "100%",
                      maxWidth: "150px",
                      maxHeight: "200px",
                      margin: "2px 0",
                    }}
                  />
                  <Field
                    type="file"
                    id="image"
                    accept="image/png,image/jpeg,image/jpg"
                    name="image"
                    onChange={handleImageIDPreview}
                  />
                  {values.image && values.image.length > 0 && (
                    <div>{values.image}</div>
                    )}
                  {errors.image && errors.image.length > 0 && (
                    <div>{errors.image}</div>
                  )}
                </div>
                <div className="field" >
                  <label htmlFor="image">
                    *Take a selfie while holding the ID submitted above.
                  </label>
                  <img
                    src={selfieImagePreview}
                    alt="ID Selfie"
                    style={{
                      width: "100%",
                      maxWidth: "150px",
                      maxHeight: "200px",
                      margin: "2px 0",
                    }}
                  />
                  <Webcam name='selfieImage' audio={false} videoConstraints={{ facingMode: "user" }} ref={webcamRef} />
                  <br/>
                  <button type="button" className='button' onClick={handleImageSelfiePreview} title='Capture image'><BsCameraFill size={35} title='ok' name='ok'/></button>

                  {errors.selfieImage && errors.selfieImage.length > 0 && (
                    <div>{errors.selfieImage}</div>
                  )}
                </div>
                <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 5 }}>
                    {activeStep === 0 && (
                      <Button onClick={handleBack} sx={{ mr: 18, mt:2 }}>&nbsp;</Button>
                    )}
                    <Button variant="contained" type='submit' sx={{ ml: 2, pl: 2}} disabled={isLoading}>
                      Next <BsChevronRight size={25}/>
                      <BeatLoader
                        color={Constant.spinner_color}
                        loading={isLoading}
                        cssOverride={{
                          textAlign: 'center',
                          marginTop: '5px',
                          marginBottom: '5px',
                        }}/>
                    </Button>
                  </Box>
                {/* {
                  <button type="submit" disabled={isLoading}>
                    {!isLoading && <span>Submit & Next</span>}
                    <BeatLoader
                      color={Constant.spinner_color}
                      loading={isLoading}
                      cssOverride={{
                        textAlign: 'center',
                        marginTop: '5px',
                        marginBottom: '5px',
                      }}/>
                  </button>
                } */}
            </form>
            )}
          </Formik>
        </Typography>
        }
        {activeStep === 1 && 
        <Typography>
          <Formik
            initialValues={{
              address: address,
              address2: secondaryAddress,
              postCode: postCode,
              city: city,
              country: country,
              addressFile: "",
              token: idToken
           }}
            onSubmit={(values) => {
              handleSubmitAddressVerification(values);
            }}
            validationSchema={verifyAddressSchema}>
            {({ handleSubmit, values, handleChange, errors, touched }) => (
              <form onSubmit={handleSubmit}>
                <div className={`field`}>
                  <label htmlFor="address">Address*</label>
                  <input
                    name="address"
                    id="address"
                    type='address'
                    placeholder="Main address"
                    value={values.address}
                    onChange={handleChange}
                    required
                  />
                  {errors.address && touched.address ? (
                    <div className="error">{errors.address}</div>
                  ) : null}
                </div>
                <div className={`field`}>
                  <label htmlFor="address2">Secondary Address</label>
                  <input
                    name="address2"
                    id="address2"
                    placeholder="Street 2"
                    value={values.address2}
                    onChange={handleChange}
                  />
                  {errors.address2 && touched.address2 ? (
                    <div className="error">{errors.address2}</div>
                  ) : null}
                  {/* <Link to={"/"} className={'forgot-password'}>Forgot password?</Link> */}
                </div>
                <div className={`field`}>
                  <label htmlFor="postCode">Postal Code</label>
                  <input
                    name="postCode"
                    id="postCode"
                    type='number'
                    placeholder="Post code"
                    value={values.postCode}
                    onChange={handleChange}
                  />
                  {errors.postCode && touched.postCode ? (
                    <div className="error">{errors.postCode}</div>
                  ) : null}
                </div>
                <div className={`field`}>
                  <label htmlFor="city">City*</label>
                  <input
                    name="city"
                    id="city"
                    type='address'
                    placeholder="City"
                    value={values.city}
                    onChange={handleChange}
                    required
                  />
                  {errors.city && touched.city ? (
                    <div className="error">{errors.city}</div>
                  ) : null}
                </div>
                <div className={`field`}>
                  <label htmlFor="country">Select Country*</label>
                  <Field
                    name="country"
                    id="country"
                    as="select"
                    value={values.country}
                    onChange={handleChange}
                    className="__form-input"
                    required>
                    <option value="" defaultValue={values.country}>Select a country</option>
                    {countries.map((country) => (
                      <option key={country.value} value={country.value}>
                        {country.label}
                      </option>
                    ))}
                  </Field>
                  {errors.country && touched.country ? (
                    <div className="error">{errors.country}</div>
                  ) : null}
                </div>
                <div className="field">
                  <label htmlFor="addressFile">
                    Upload proof of address (A utility bill, receipt or documenting clearly indicating the address above)*
                  </label>
                  <span style={{ fontWeight: 'bold'}}>{addressFilePlaceholder}</span>
                  <Field
                    type="file"
                    id="addressFile"
                    accept="image/*,application/pdf"
                    name="addressFile"
                    onChange={handleAddressFile}
                  />
                  {values.addressFile && values.addressFile.length > 0 && (
                    <div>{values.addressFile}</div>
                    )}
                  {errors.addressFile && errors.addressFile.length > 0 && (
                    <div>{errors.addressFile}</div>
                  )}
                </div>
                <Box sx={{ display: 'flex', justifyContent: 'space-around', p: 0, mt: 5 }}>
                    {activeStep !== 0 && (
                      <Button onClick={handleBack} sx={{ mr: 18, mt:2 }}><BsChevronLeft size={25} />&nbsp;</Button>
                    )}
                    <Button variant="contained" type='submit' sx={{ ml: -5, pl: 5}} disabled={isLoading}>
                      Next <BsChevronRight size={25}/>
                      <BeatLoader
                        color={Constant.spinner_color}
                        loading={isLoading}
                        cssOverride={{
                          textAlign: 'center',
                          marginTop: '5px',
                          marginBottom: '5px',
                        }}/>
                    </Button>
                  </Box>
                {/* {
                <button type="submit" disabled={isLoading}>
                  {!isLoading && <span>Submit & Next</span>}
                  <BeatLoader
                    color={Constant.spinner_color}
                    loading={isLoading}
                    cssOverride={{
                      textAlign: 'center',
                      marginTop: '5px',
                      marginBottom: '5px',
                    }}/>
                </button>
              } */}
              </form>
            )}
          </Formik>
        </Typography>
        }
        {activeStep === 2 && 
        <Typography>
          <Formik
            initialValues={{
              professionalFile: '',
              token: idToken
            }}
            onSubmit={(values) => {
              handleSubmitProVerification(values);
            }}>
            {({ handleSubmit, values, handleChange, errors, touched }) => (
              <form onSubmit={handleSubmit}>
                  <div className="field">
                    <label htmlFor="professionalFile">
                      Upload your professional document (Can be an employment letter, pay slip, business certificate or other attestations)
                    </label>
                    <span>{professionalFilePlaceholder}</span>
                    <Field
                      type="file"
                      id="professionalFile"
                      accept="image/*,application/pdf"
                      name="professionalFile"
                      onChange={handleProfessionalFile}
                    />
                    {values.professionalFile && values.professionalFile.length > 0 && (
                      <div>{values.professionalFile}</div>
                      )}
                    {errors.professionalFile && errors.professionalFile.length > 0 && (
                      <div>{errors.professionalFile}</div>
                    )}
                  </div>
                  <Box sx={{ display: 'flex', justifyContent: 'space-around', p: 0 }}>
                    {activeStep !== 0 && (
                      <Button onClick={handleBack} sx={{ mr: 18, mt:2 }}><BsChevronLeft size={25} />&nbsp;</Button>
                    )}
                    <Button variant="contained" type='submit' sx={{ ml: -5, pl: 5}} disabled={isLoading}>
                      Submit <BsCheck size={35}/>
                      <BeatLoader
                        color={Constant.spinner_color}
                        loading={isLoading}
                        cssOverride={{
                          textAlign: 'center',
                          marginTop: '5px',
                          marginBottom: '5px',
                        }}/>
                    </Button>
                  </Box>
                  {/* {
                    <button type="submit" disabled={isLoading}>
                      {!isLoading && <span>Submit & Complete</span>}
                      <BeatLoader
                        color={Constant.spinner_color}
                        loading={isLoading}
                        cssOverride={{
                          textAlign: 'center',
                          marginTop: '5px',
                          marginBottom: '5px',
                        }}/>
                    </button>
                  } */}
              </form>
            )}
          </Formik>
        </Typography>  
        }{
          activeStep === 3 && <>
          <div style={{ display: 'flex', justifyContent: 'center'}}>
            <BsPersonCheck size={200} color='#0b0' />
          </div>
          <br/>
          <span style={{fontSize: 20}}>{successMessage}</span>
          </>
        }
            
      </Content>
      {activeStep < steps.length - 1 && <Button onClick={handleSkip} sx={{ mt: 2 }}>
        Skip <BsChevronDoubleRight/>
      </Button>}
    </Box>
  );
}

export default KycForm;
